import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAdminUsers, getUserStatistics, toAdminUsersApiError } from '../api/adminUsersApi';
import {
  USER_ROLES,
  type AdminUserPage,
  type AdminUserQuery,
  type UserRole,
  type UserStatistics,
  type UserStatus,
} from '../types';

const PAGE_SIZE = 10;
const EMPTY_PAGE: AdminUserPage = {
  currentPage: 1,
  totalPages: 0,
  pageSize: PAGE_SIZE,
  totalElements: 0,
  data: [],
};

const isUserRole = (value: string | null): value is UserRole =>
  !!value && USER_ROLES.includes(value as UserRole);

const isUserStatus = (value: string | null): value is UserStatus =>
  value === 'ACTIVE' || value === 'INACTIVE';

export const useAdminUsers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageData, setPageData] = useState<AdminUserPage>(EMPTY_PAGE);
  const [statistics, setStatistics] = useState<UserStatistics | null>(null);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [isStatisticsLoading, setIsStatisticsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const listRequestId = useRef(0);
  const statisticsRequestId = useRef(0);

  const query = useMemo<AdminUserQuery>(() => {
    const requestedPage = Number(searchParams.get('page'));
    const role = searchParams.get('role');
    const status = searchParams.get('status');
    return {
      search: searchParams.get('search')?.trim() || undefined,
      role: isUserRole(role) ? role : undefined,
      status: isUserStatus(status) ? status : undefined,
      page: Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1,
      size: PAGE_SIZE,
      sort: 'createdAt,desc',
    };
  }, [searchParams]);

  const loadUsers = useCallback(async () => {
    const requestId = ++listRequestId.current;
    setIsTableLoading(true);
    setLoadError('');
    try {
      const response = await getAdminUsers(query);
      if (requestId === listRequestId.current) {
        setPageData(response);
        setHasLoaded(true);
      }
    } catch (error: unknown) {
      if (requestId === listRequestId.current) {
        setLoadError(toAdminUsersApiError(error).message);
        setHasLoaded(true);
      }
    } finally {
      if (requestId === listRequestId.current) setIsTableLoading(false);
    }
  }, [query]);

  const loadStatistics = useCallback(async () => {
    const requestId = ++statisticsRequestId.current;
    setIsStatisticsLoading(true);
    try {
      const response = await getUserStatistics();
      if (requestId === statisticsRequestId.current) setStatistics(response);
    } catch {
      if (requestId === statisticsRequestId.current) setStatistics(null);
    } finally {
      if (requestId === statisticsRequestId.current) setIsStatisticsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUsers();
    return () => {
      listRequestId.current += 1;
    };
  }, [loadUsers]);

  useEffect(() => {
    void loadStatistics();
    return () => {
      statisticsRequestId.current += 1;
    };
  }, [loadStatistics]);

  const updateFilters = useCallback((updates: Record<string, string | undefined>) => {
    const nextParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) nextParams.set(key, value);
      else nextParams.delete(key);
    });
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const setSearch = useCallback((search: string) => {
    updateFilters({ search: search.trim() || undefined, page: undefined });
  }, [updateFilters]);

  const setRole = useCallback((role?: UserRole) => {
    updateFilters({ role, page: undefined });
  }, [updateFilters]);

  const setStatus = useCallback((status?: UserStatus) => {
    updateFilters({ status, page: undefined });
  }, [updateFilters]);

  const setPage = useCallback((page: number) => {
    updateFilters({ page: page > 1 ? String(page) : undefined });
  }, [updateFilters]);

  const refresh = useCallback(async () => {
    await Promise.all([loadUsers(), loadStatistics()]);
  }, [loadStatistics, loadUsers]);

  return {
    query,
    pageData,
    statistics,
    isInitialLoading: !hasLoaded && isTableLoading,
    isTableLoading,
    isStatisticsLoading,
    loadError,
    setSearch,
    setRole,
    setStatus,
    setPage,
    refresh,
    retry: loadUsers,
  };
};
